<script lang="ts" setup>
import { AnswerType } from '@/models/Forms'
import {
  UICheckbox,
  UIInput,
  UIRadio,
  UIRadioGroup,
  UITextSmMedium,
  UITextSmNormal,
} from '@gohighlevel/ghl-ui'
import { useI18n } from 'vue-i18n'

interface MembershipQuestion {
  question: string
  answerType: AnswerType
  answerOptions?: string[]
}

// Separate types for different kinds of answers
type TextAnswer = string
type SingleSelectAnswer = string
type MultiSelectAnswer = boolean[]
type MembershipAnswer = TextAnswer | SingleSelectAnswer | MultiSelectAnswer

const props = defineProps({
  questions: {
    type: Array as () => MembershipQuestion[],
    required: true,
  },
  answers: {
    type: Array as () => MembershipAnswer[],
    required: true,
  },
  isReadOnly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:answers'])
const { t } = useI18n()

const updateAnswer = (questionIndex: number, value: string) => {
  if (props.isReadOnly) return
  const newAnswers = [...props.answers]
  newAnswers[questionIndex] = value
  emit('update:answers', newAnswers)
}

const updateMultiSelectAnswer = (
  questionIndex: number,
  optionIndex: number,
  value: boolean
) => {
  if (props.isReadOnly) return
  const newAnswers = [...props.answers]
  if (Array.isArray(newAnswers[questionIndex])) {
    newAnswers[questionIndex] = [...(newAnswers[questionIndex] as boolean[])]
    ;(newAnswers[questionIndex] as boolean[])[optionIndex] = value
    emit('update:answers', newAnswers)
  }
}

// Helper function to safely get single select/text answer
const getSingleAnswer = (answer: MembershipAnswer): string => {
  return typeof answer === 'string' ? answer : ''
}
</script>

<template>
  <div class="py-2">
    <li
      v-for="(question, questionIndex) in questions"
      :key="questionIndex"
      class="list-none"
    >
      <div class="py-2">
        <UITextSmMedium class="py-2 capitalize text-communities-font-primary">
          {{ question.question }}
        </UITextSmMedium>

        <!-- Text Input -->
        <div v-if="question.answerType === AnswerType.TEXT_BOX">
          <UIInput
            :id="`members_questions_answer_${questionIndex}`"
            :value="getSingleAnswer(answers[questionIndex])"
            :placeholder="t('groupInfo.placeholders.yourAnswer')"
            class="input-style"
            :maxlength="150"
            :show-count="true"
            :rows="4"
            :cols="100"
            type="textarea"
            :disabled="isReadOnly"
            @update:value="value => updateAnswer(questionIndex, value)"
          />
        </div>

        <!-- Single Select -->
        <div v-if="question.answerType === AnswerType.SINGLE_SELECT">
          <UIRadioGroup
            :value="getSingleAnswer(answers[questionIndex])"
            :disabled="false"
            @update:value="value => updateAnswer(questionIndex, value)"
          >
            <UIRadio
              v-for="option in question.answerOptions"
              :id="`radio-${questionIndex}-${option}`"
              :key="option"
              :value="option"
              class="ml-2 flex w-full items-center"
            >
              <UITextSmNormal class="text-communities-font-primary">{{
                option
              }}</UITextSmNormal>
            </UIRadio>
          </UIRadioGroup>
        </div>

        <!-- Multiple Select -->
        <div v-if="question.answerType === AnswerType.MULTIPLE_SELECT">
          <li
            v-for="(
              answerOption, answerOptionIndex
            ) in question.answerOptions || []"
            :key="answerOptionIndex"
          >
            <UICheckbox
              :id="`checkbox-multiselect-answers-${questionIndex}-${answerOptionIndex}`"
              :checked="
                Array.isArray(answers[questionIndex]) &&
                answers[questionIndex][answerOptionIndex] === true
              "
              :disabled="false"
              class="ml-2 items-center"
              @update:checked="
                value =>
                  updateMultiSelectAnswer(
                    questionIndex,
                    answerOptionIndex,
                    value
                  )
              "
            >
              <UITextSmNormal class="text-communities-font-primary">{{
                answerOption
              }}</UITextSmNormal>
            </UICheckbox>
          </li>
        </div>
      </div>
    </li>
  </div>
</template>
